// Generic registry-driven workspace for areas without bespoke deep
// workspaces (SSI, PI, Wills, Family, Appeals, DUI, plus tier-3 fallbacks).
//
// Composition: ClientHero + AISummary + SwimLanes + DocWall (shared) +
// area-config cards (forms, hero metric, big tip, actions) + a generic
// data wall over matter.workspace so structured fields surface without
// per-area component code.
//
// Per Robert's universal-formatting principle: one engine, registry-driven
// variation only. No per-area branching here.

import type { Client, Matter, PracticeArea } from "../../data/types";
import { AISummary } from "./AISummary";
import { MatterHero } from "./MatterHero";
import { DocWall } from "./DocWall";
import { SwimLanes } from "./SwimLanes";
import type { MatterWithClient } from "../../api/mock";
import { isPastEvent } from "../../api/firmDeadlines";
import { EMPTY, SEP } from "../chrome/SepList";

interface Props {
  matter: Matter;
  client: Client | null | undefined;
  pa: PracticeArea | null;
  templateId: string | null;
}

function humanizeKey(k: string): string {
  return k
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function renderValue(v: unknown): string {
  if (v == null) return EMPTY;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return "(empty)";
    if (v.every((x) => typeof x === "string" || typeof x === "number")) {
      return v.join(", ");
    }
    return `${v.length} item${v.length === 1 ? "" : "s"}`;
  }
  if (typeof v === "object") {
    const keys = Object.keys(v as Record<string, unknown>);
    return keys.length === 0 ? "(empty)" : `{${keys.length} field${keys.length === 1 ? "" : "s"}}`;
  }
  return String(v);
}

function flattenWorkspace(
  ws: Record<string, unknown> | undefined,
): Array<{ key: string; value: unknown; nested?: Array<{ key: string; value: unknown }> }> {
  if (!ws) return [];
  return Object.entries(ws).map(([k, v]) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const nested = Object.entries(v as Record<string, unknown>).map(([nk, nv]) => ({
        key: nk,
        value: nv,
      }));
      return { key: k, value: v, nested };
    }
    return { key: k, value: v };
  });
}

export function RegistryWorkspace({ matter, client, pa, templateId }: Props) {
  const wsRows = flattenWorkspace(matter.workspace);
  const caseId = (matter as Partial<MatterWithClient>).mongoId;
  const nextDeadline = matter.deadlines?.find((d) => !isPastEvent(d));

  return (
    <>
      <MatterHero matter={matter} client={client} pa={pa} templateId={templateId} />
      <AISummary summary={matter.aiSummary} caseId={caseId} />
      <SwimLanes matter={matter} pa={pa} />
      <DocWall documents={matter.documents} caseId={caseId} driveFolder={matter.driveFolder} />

      {/* Practice-area key card - heroMetric + bigTip + actions */}
      {pa && (
        <div className="cc-card" style={{ marginBottom: 14 }}>
          <div className="cc-card-head">
            <div
              className="cc-card-title"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <span style={{ color: pa.accent }}>{pa.icon}</span>
              {pa.label} workspace
            </div>
            <div className="cc-card-sub">
              {SEP}
              {pa.tagline}
            </div>
          </div>
          <div style={{ padding: "14px 18px 16px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
                marginBottom: 12,
              }}
            >
              {pa.heroMetricLabel && (
                <div>
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--muted)",
                    }}
                  >
                    {pa.heroMetricLabel}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {String(matter.workspace?.[pa.heroMetric] ?? EMPTY)}
                  </div>
                </div>
              )}
              {nextDeadline && (
                <div>
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--muted)",
                    }}
                  >
                    Next deadline
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{nextDeadline.label}</div>
                </div>
              )}
            </div>
            {pa.bigTip && (
              <div
                // A full outline: the practice's pale tint under a bar reads
                // as the AI panel, and this tip is the app's.
                style={{
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: `1px solid ${pa.accentStrong}`,
                  fontSize: 12.5,
                  color: "var(--ink-2)",
                  lineHeight: 1.5,
                  marginBottom: 12,
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    color: "var(--pa-accent-strong, var(--ink))",
                    marginRight: 6,
                  }}
                >
                  TIP
                </span>
                {pa.bigTip}
              </div>
            )}
            {pa.actions && pa.actions.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {pa.actions.map((a, i) => (
                  <button
                    key={i}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 6,
                      fontSize: 11.5,
                      fontWeight: 600,
                      background: i === 0 ? "var(--pa-accent-strong, var(--muted))" : "var(--bg)",
                      color: i === 0 ? "#fff" : "var(--ink-2)",
                      border:
                        i === 0
                          ? "1px solid var(--pa-accent-strong, var(--muted))"
                          : "1px solid var(--line)",
                    }}
                  >
                    {a}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 14,
        }}
      >
        {/* Forms catalog */}
        {pa?.forms && pa.forms.length > 0 && (
          <div className="cc-card">
            <div className="cc-card-head">
              <div className="cc-card-title">Forms catalog</div>
              <div className="cc-card-sub">
                {SEP}
                {pa.forms.length} forms
              </div>
            </div>
            <div style={{ padding: "8px 18px 14px" }}>
              {pa.forms.map((f, i) => (
                <div
                  key={i}
                  style={{
                    padding: "6px 0",
                    borderBottom: i === pa.forms.length - 1 ? "none" : "1px solid var(--line-2)",
                    fontSize: 12,
                    color: "var(--ink-2)",
                  }}
                >
                  {f}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Workspace data wall */}
        <div className="cc-card">
          <div className="cc-card-head">
            <div className="cc-card-title">Workspace data</div>
            <div className="cc-card-sub">
              {SEP}
              {wsRows.length} field{wsRows.length === 1 ? "" : "s"} from matter
            </div>
          </div>
          <div style={{ padding: "8px 18px 14px" }}>
            {wsRows.length === 0 ? (
              <div style={{ fontSize: 12.5, color: "var(--muted)", padding: "10px 0" }}>
                No structured workspace data on this matter yet.
              </div>
            ) : (
              wsRows.map(({ key, value, nested }) => (
                <div
                  key={key}
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid var(--line-2)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--muted)",
                      marginBottom: 4,
                    }}
                  >
                    {humanizeKey(key)}
                  </div>
                  {nested ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "max-content 1fr",
                        gap: "2px 12px",
                        fontSize: 12,
                      }}
                    >
                      {nested.map(({ key: nk, value: nv }) => (
                        <div key={nk} style={{ display: "contents" }}>
                          <span style={{ color: "var(--muted)" }}>{humanizeKey(nk)}</span>
                          <span style={{ color: "var(--ink)" }}>{renderValue(nv)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12.5, color: "var(--ink)" }}>{renderValue(value)}</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
